import { useState } from 'react';
import { cn } from '@/lib/utils';
import { FeedPanel } from './RightRail';
import type { FeedEvent, FeedFilter } from '@/lib/nflDraftFeed';

/**
 * SAB-871: the mobile CHAT tab, with the FEED folded in as a segmented control.
 *
 * This is the arrangement MobileDraftRoom's own header comment specifies -- "Feed folds into
 * the CHAT tab as a segmented control, keeping the bar at four items" -- so the tab bar stays
 * at four and the feed is still reachable.
 *
 * The feed half is the DESKTOP FeedPanel, exported from RightRail rather than reimplemented.
 * A second feed renderer would be free to drift from the first on filters, event copy and
 * ordering, and the feed is the room's record of what happened.
 *
 * Which half is showing is LOCAL state, deliberately. It is a within-tab view preference,
 * not a room-level one: it does not survive a tab switch and is not persisted, unlike
 * centerView/railTab which nflDraftRoomPrefs stores per league. Adding a fifth persisted
 * preference for a two-way toggle inside one tab would be more machinery than the choice
 * deserves.
 */

export interface MobileChatPanelProps {
  chatPanel: React.ReactNode;
  feed: FeedEvent[];
  feedFilter: FeedFilter;
  onFeedFilterChange: (f: FeedFilter) => void;
  unreadFeed?: number;
}

type Half = 'chat' | 'feed';

export default function MobileChatPanel({
  chatPanel, feed, feedFilter, onFeedFilterChange, unreadFeed = 0,
}: MobileChatPanelProps) {
  const [half, setHalf] = useState<Half>('chat');

  return (
    <div data-testid="mobile-chat-panel" className="flex h-full min-h-0 flex-col">
      <div
        role="tablist"
        aria-label="Chat or feed"
        className="flex-none m-3 mb-2 inline-flex rounded-[10px] border border-border bg-muted p-[3px]"
      >
        {(['chat', 'feed'] as Half[]).map(h => (
          <button
            key={h}
            role="tab"
            aria-selected={half === h}
            data-testid={`mobile-chat-seg-${h}`}
            onClick={() => setHalf(h)}
            className={cn(
              // 44px minimum, same rule as the tab bar and action bar in MobileDraftRoom.
              'relative min-h-11 flex-1 rounded-[7px] px-4 font-display text-[11.5px] font-bold uppercase',
              half === h ? 'bg-primary text-[#12130f]' : 'text-muted-foreground',
            )}
          >
            {h}
            {h === 'feed' && unreadFeed > 0 && half !== 'feed' && (
              <span
                data-testid="mobile-feed-unread"
                className="absolute right-2 top-1.5 h-1.5 w-1.5 rounded-full bg-damage"
              />
            )}
          </button>
        ))}
      </div>

      {/* min-h-0 so the live chat's own scroller works instead of pushing the segmented
          control off the top of the panel. */}
      <div className="min-h-0 flex-1">
        {half === 'chat' ? (
          <div data-testid="mobile-chat-half" className="h-full min-h-0">{chatPanel}</div>
        ) : (
          <div data-testid="mobile-feed-half" className="flex h-full min-h-0 flex-col">
            {/* FeedPanel is typed against the whole RightRailProps surface but reads only
                these three. Cast rather than thread a dozen unrelated rail props through the
                mobile room. */}
            <FeedPanel {...({ feed, feedFilter, onFeedFilterChange } as unknown as Parameters<typeof FeedPanel>[0])} />
          </div>
        )}
      </div>
    </div>
  );
}
